/// <reference types="google.maps" />

import React, {useCallback, useMemo, useState} from 'react';
import {Input, InputProps, VStack, Box, List, ListItem, useTheme} from '@chakra-ui/react';
import {useAutocompleteSuggestions} from '../../hooks/useAutocompleteSuggestions';

interface GooglePlacesAutocompleteProps extends Omit<InputProps, 'onChange'> {
  onPlaceSelected: (place: any) => void;
  onAddressChange?: (address: string) => void;
  value?: string;
}

const GooglePlacesAutocomplete: React.FC<GooglePlacesAutocompleteProps> = ({
  onPlaceSelected,
  onAddressChange,
  value = '',
  ...inputProps
}) => {
  const theme = useTheme();
  const [inputValue, setInputValue] = useState(value);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const autocompleteOptions = useMemo(() => ({
    includedPrimaryTypes: ['street_address', 'premise', 'subpremise'],
    includedRegionCodes: ['us'],
    locationBias: {
      north: 33.3,    // Oceanside/North County
      south: 32.5,    // Chula Vista/near border
      east: -116.7,   // Alpine/East County
      west: -117.6    // Pacific Ocean
    }
  }), []);

  const {suggestions, resetSession} = useAutocompleteSuggestions(inputValue, autocompleteOptions);

  // Sync external value changes
  React.useEffect(() => {
    setInputValue(value);
  }, [value]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setInputValue(newValue);
      setShowSuggestions(true);

      if (onAddressChange) {
        onAddressChange(newValue);
      }
    },
    [onAddressChange]
  );

  const buildFormattedAddress = useCallback(
    (addressComponents: google.maps.places.AddressComponent[]): string => {
      const addressParts: string[] = [];

      // Street number and route (e.g., "123 Main Street")
      const streetNumber =
        addressComponents.find((c) => c.types.includes('street_number'))?.longText || '';
      const route = addressComponents.find((c) => c.types.includes('route'))?.longText || '';
      if (streetNumber && route) {
        addressParts.push(`${streetNumber} ${route}`);
      } else if (route) {
        addressParts.push(route);
      }

      // City
      const city = addressComponents.find((c) => c.types.includes('locality'))?.longText || '';
      if (city) {
        addressParts.push(city);
      }

      // State (use short_name for abbreviation)
      const state =
        addressComponents.find((c) => c.types.includes('administrative_area_level_1'))?.shortText ||
        '';
      if (state) {
        addressParts.push(state);
      }

      // Zip code
      const zipCode =
        addressComponents.find((c) => c.types.includes('postal_code'))?.longText || '';
      if (zipCode) {
        addressParts.push(zipCode);
      }

      return addressParts.join(', ');
    },
    []
  );

  const handleSuggestionClick = useCallback(
    async (suggestion: google.maps.places.AutocompleteSuggestion) => {
      if (!suggestion.placePrediction) return;

      const place = suggestion.placePrediction.toPlace();

      // Fetch place details including address components
      await place.fetchFields({
        fields: ['addressComponents', 'location', 'viewport', 'formattedAddress'],
      });

      // Build formatted address from components using long_name to match database storage
      const cleanedAddress = place.addressComponents
        ? buildFormattedAddress(place.addressComponents)
        : place.formattedAddress || '';

      setInputValue(cleanedAddress);
      setShowSuggestions(false);

      // Reset session token after place selection (important for billing)
      resetSession();

      // Convert new AddressComponent format to legacy format for backward compatibility
      const legacyAddressComponents =
        place.addressComponents?.map((component) => ({
          long_name: component.longText || '',
          short_name: component.shortText || '',
          types: component.types,
        })) || [];

      // Create a place object compatible with the legacy interface (callers do
      // `geometry.location.lat()` / `.lng()` and expect a number back).
      const lat =
        typeof (place.location as any)?.lat === 'function'
          ? (place.location as any).lat()
          : (place.location as any)?.lat;
      const lng =
        typeof (place.location as any)?.lng === 'function'
          ? (place.location as any).lng()
          : (place.location as any)?.lng;

      const placeData = {
        address_components: legacyAddressComponents,
        formatted_address: cleanedAddress,
        geometry: place.location
          ? {
              location: {
                lat: () => lat,
                lng: () => lng,
              },
            }
          : undefined,
        viewport: place.viewport,
      };

      onPlaceSelected(placeData);

      if (onAddressChange) {
        onAddressChange(cleanedAddress);
      }
    },
    [onPlaceSelected, onAddressChange, resetSession, buildFormattedAddress]
  );

  // Filter to only show place predictions
  const placePredictions = useMemo(
    () => suggestions.filter((s) => s.placePrediction).map((s) => s.placePrediction!),
    [suggestions]
  );

  const handleInputFocus = useCallback(() => {
    if (inputValue && placePredictions.length > 0) {
      setShowSuggestions(true);
    }
  }, [inputValue, placePredictions.length]);

  const handleInputBlur = useCallback(() => {
    // Delay to allow click on suggestion to register
    setTimeout(() => setShowSuggestions(false), 200);
  }, []);

  return (
    <Box position="relative" width="100%">
      <Input
        value={inputValue}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        autoComplete="off"
        {...inputProps}
      />

      {showSuggestions && placePredictions.length > 0 && (
        <List
          position="absolute"
          top="100%"
          left={0}
          right={0}
          zIndex={1000}
          bg="white"
          boxShadow="0 2px 6px rgba(0,0,0,0.3)"
          border="1px solid"
          borderColor={theme.colors.gray[300]}
          borderRadius="2px"
          maxH="300px"
          overflowY="auto"
          mt={1}
          py={1}>
          {placePredictions.map((prediction, index) => (
            <ListItem
              key={prediction.placeId}
              px={4}
              py={2.5}
              cursor="pointer"
              fontSize="14px"
              color={theme.colors.gray[700]}
              transition="background-color 0.1s"
              _hover={{
                bg: theme.colors.gray[100],
              }}
              onClick={() => handleSuggestionClick(suggestions[index])}>
              {prediction.text.text}
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
};

export default GooglePlacesAutocomplete;
